import {
  UsersRoundIcon,
  LightbulbIcon,
  HandHeartIcon,
  MegaphoneIcon,
  HandshakeIcon,
  LandmarkIcon,
  NetworkIcon,
  GraduationCapIcon,
  UsersIcon,
  BriefcaseIcon,
  SparklesIcon,
  CompassIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface Pillar {
  icon: LucideIcon
  title: string
  titleEs: string
  text: string
  textEs: string
}

export const PILLARS: Pillar[] = [
  {
    icon: UsersRoundIcon,
    title: 'National Connections',
    titleEs: 'Conexiones Nacionales',
    text: 'Connect organizations, leaders, professionals, and trusted partners across the United States and Honduras to strengthen relationships, share opportunities, and encourage collaboration.',
    textEs:
      'Conectar organizaciones, líderes, profesionales y socios de confianza en los Estados Unidos y Honduras para fortalecer relaciones, compartir oportunidades y fomentar la colaboración.',
  },
  {
    icon: LightbulbIcon,
    title: 'Organizational Capacity',
    titleEs: 'Capacidad Organizacional',
    text: 'Strengthen organizations through shared resources, peer learning, and access to expertise in governance, fundraising, planning, communications, and leadership development.',
    textEs:
      'Fortalecer las organizaciones mediante recursos compartidos, aprendizaje entre pares y acceso a experiencia en gobernanza, recaudación de fondos, planificación, comunicaciones y desarrollo de liderazgo.',
  },
  {
    icon: HandHeartIcon,
    title: 'Community Engagement',
    titleEs: 'Participación Comunitaria',
    text: 'Create opportunities to better connect with the communities organizations serve, whether in the United States or Honduras, while sharing local priorities, programs, events, and opportunities across the Network.',
    textEs:
      'Crear oportunidades para conectar mejor con las comunidades que sirven las organizaciones, ya sea en los Estados Unidos u Honduras, compartiendo prioridades locales, programas, eventos y oportunidades en toda la Red.',
  },
  {
    icon: MegaphoneIcon,
    title: 'National Visibility',
    titleEs: 'Visibilidad Nacional',
    text: 'Elevate the work, leadership, and impact of participating organizations, including U.S.-based organizations supporting programs and communities in Honduras, through storytelling, media outreach, events, and digital platforms.',
    textEs:
      'Elevar el trabajo, el liderazgo y el impacto de las organizaciones participantes, incluidas las organizaciones con sede en EE. UU. que apoyan programas y comunidades en Honduras, mediante narrativa, medios de comunicación, eventos y plataformas digitales.',
  },
  {
    icon: HandshakeIcon,
    title: 'Strategic Partnerships',
    titleEs: 'Alianzas Estratégicas',
    text: 'Build relationships with foundations, companies, institutions, and other partners to explore shared funding opportunities, joint initiatives, and collaborations that can strengthen work in both the United States and Honduras.',
    textEs:
      'Construir relaciones con fundaciones, empresas, instituciones y otros socios para explorar oportunidades de financiamiento compartido, iniciativas conjuntas y colaboraciones que fortalezcan el trabajo en los Estados Unidos y Honduras.',
  },
  {
    icon: LandmarkIcon,
    title: 'Civic Engagement',
    titleEs: 'Participación Cívica',
    text: 'Advance nonpartisan civic education, dialogue, research, and engagement with institutions and policymakers on issues affecting Honduran-American communities and the broader relationship between the United States and Honduras.',
    textEs:
      'Impulsar la educación cívica no partidista, el diálogo, la investigación y la interacción con instituciones y responsables de políticas sobre temas que afectan a las comunidades hondureño-americanas y la relación entre los Estados Unidos y Honduras.',
  },
]

export interface Participation {
  icon: LucideIcon
  title: string
  titleEs: string
  text: string
  textEs: string
}

export const PARTICIPATION: Participation[] = [
  {
    icon: NetworkIcon,
    title: 'Connect',
    titleEs: 'Conectar',
    text: 'Participate in national and regional conversations, peer exchanges, introductions, and networking opportunities with organizations and leaders across the United States and Honduras.',
    textEs:
      'Participar en conversaciones nacionales y regionales, intercambios entre pares, presentaciones y oportunidades de networking con organizaciones y líderes en los Estados Unidos y Honduras.',
  },
  {
    icon: GraduationCapIcon,
    title: 'Learn & Strengthen',
    titleEs: 'Aprender y Fortalecer',
    text: 'Access shared resources, trainings, peer learning, and connections to professionals and advisors who can help strengthen organizational capacity.',
    textEs:
      'Acceder a recursos compartidos, capacitaciones, aprendizaje entre pares y conexiones con profesionales y asesores que ayuden a fortalecer la capacidad organizacional.',
  },
  {
    icon: UsersIcon,
    title: 'Collaborate',
    titleEs: 'Colaborar',
    text: 'Explore opportunities to work with other organizations on shared initiatives, programs, community priorities, research, and events.',
    textEs:
      'Explorar oportunidades para trabajar con otras organizaciones en iniciativas compartidas, programas, prioridades comunitarias, investigación y eventos.',
  },
  {
    icon: BriefcaseIcon,
    title: 'Build Partnerships',
    titleEs: 'Construir Alianzas',
    text: 'Explore connections with foundations, companies, institutions, and other partners, including potential collaborative funding and partnership opportunities.',
    textEs:
      'Explorar conexiones con fundaciones, empresas, instituciones y otros socios, incluyendo posibles oportunidades de financiamiento y alianzas colaborativas.',
  },
  {
    icon: SparklesIcon,
    title: 'Amplify Impact',
    titleEs: 'Amplificar el Impacto',
    text: 'Share organizational programs, accomplishments, events, and stories to help elevate the work already happening across our communities.',
    textEs:
      'Compartir programas, logros, eventos e historias de la organización para ayudar a elevar el trabajo que ya se realiza en nuestras comunidades.',
  },
  {
    icon: CompassIcon,
    title: 'Help Shape the Network',
    titleEs: 'Ayudar a Formar la Red',
    text: 'Share priorities, needs, ideas, and expertise to help guide future Network activities and areas of collaboration.',
    textEs:
      'Compartir prioridades, necesidades, ideas y experiencia para guiar las futuras actividades y áreas de colaboración de la Red.',
  },
]

export interface JoinStep {
  title: string
  titleEs: string
  lead: string
  leadEs: string
  points: string[]
  pointsEs: string[]
}

export const JOIN_STEPS: JoinStep[] = [
  {
    title: 'Connect',
    titleEs: 'Conectar',
    lead: 'The Network brings together select, mission-aligned organizations committed to strengthening Honduran-American communities and communities in Honduras. Participation is by invitation.',
    leadEs:
      'La Red reúne a organizaciones selectas y alineadas con la misión, comprometidas con fortalecer las comunidades hondureño-americanas y las comunidades en Honduras. La participación es por invitación.',
    points: [],
    pointsEs: [],
  },
  {
    title: 'Apply',
    titleEs: 'Aplicar',
    lead: 'To participate, an organization must:',
    leadEs: 'Para participar, una organización debe:',
    points: [
      'Serve Honduran-American communities and/or communities in Honduras, and maintain an organizational presence or operations in the United States.',
      'Maintain a strong reputation and record of integrity, including responsible organizational and financial practices.',
      'Complete and submit the online application.',
      'Provide additional information as needed to verify eligibility and good standing.',
      'If registered as a 501(c)(3), organizations may also be asked to provide supporting documentation, including formation documents and the most recent IRS Form 990, 990-EZ, or 990-N, as applicable.',
    ],
    pointsEs: [
      'Servir a comunidades hondureño-americanas y/o comunidades en Honduras, y mantener presencia u operaciones organizacionales en los Estados Unidos.',
      'Mantener una sólida reputación y trayectoria de integridad, incluidas prácticas organizacionales y financieras responsables.',
      'Completar y enviar la solicitud en línea.',
      'Proporcionar información adicional según sea necesario para verificar la elegibilidad y el buen estado.',
      'Si está registrada como 501(c)(3), también se le podrá solicitar documentación de respaldo, incluidos documentos de constitución y el Formulario 990, 990-EZ o 990-N del IRS más reciente, según corresponda.',
    ],
  },
  {
    title: 'Participate & Invest',
    titleEs: 'Participar e Invertir',
    lead: 'We believe a strong national network requires shared participation and collective investment, while recognizing that every organization has different capacities.',
    leadEs:
      'Creemos que una red nacional sólida requiere participación compartida e inversión colectiva, reconociendo que cada organización tiene capacidades diferentes.',
    points: [
      'Make a suggested annual contribution of $500 to help support the Network’s shared activities and infrastructure. Flexible, blended, in-kind, or other equitable participation options are available based on organizational capacity.',
      'Share organizational programs, events, needs, opportunities, and updates with the Network.',
      'Participate in convenings, trainings, working groups, and collaborative activities as relevant and able.',
    ],
    pointsEs: [
      'Realizar una contribución anual sugerida de $500 para apoyar las actividades e infraestructura compartidas de la Red. Existen opciones flexibles, combinadas, en especie u otras formas equitativas de participación según la capacidad organizacional.',
      'Compartir programas, eventos, necesidades, oportunidades y actualizaciones de la organización con la Red.',
      'Participar en convocatorias, capacitaciones, grupos de trabajo y actividades colaborativas según sea pertinente y posible.',
    ],
  },
]

export const PRESIDENT_LETTER = {
  salutation: 'To Our Community,',
  salutationEs: 'A Nuestra Comunidad,',
  paragraphs: [
    'Across this country, Hondurans are building something remarkable. In classrooms and clinics, in churches and dance halls, in small businesses and community centers, our people are showing up for one another every single day.',
    'What has been missing is not effort. It is connection. For too long, that work has happened in separate corners, unaware of how much stronger it could be together.',
    'The Honduran-American National Network exists to close that distance — to bring our organizations, leaders, and neighbors into one national community where knowledge is shared, doors are opened, and no one has to build alone.',
    'We are just beginning, and we want to build this with you, not simply for you. Whatever you carry — an organization, an idea, or simply the will to help — there is a place for you here.',
  ],
  paragraphsEs: [
    'En todo este país, los hondureños están construyendo algo extraordinario. En aulas y clínicas, en iglesias y salones de baile, en pequeños negocios y centros comunitarios, nuestra gente se presenta por los demás cada día.',
    'Lo que ha faltado no es esfuerzo. Es conexión. Durante demasiado tiempo, ese trabajo ha ocurrido en rincones separados, sin saber cuánto más fuerte podría ser en conjunto.',
    'La Red Nacional Hondureño-Americana existe para acortar esa distancia — para reunir a nuestras organizaciones, líderes y vecinos en una sola comunidad nacional donde se comparte el conocimiento, se abren puertas y nadie tiene que construir en soledad.',
    'Apenas estamos comenzando, y queremos construir esto con ustedes, no simplemente para ustedes. Lo que sea que traigan — una organización, una idea, o simplemente la voluntad de ayudar — aquí hay un lugar para ustedes.',
  ],
  closing: 'With appreciation and optimism,',
  closingEs: 'Con aprecio y optimismo,',
  name: 'Jay Miranda',
  role: 'President & CEO, Honduran-American Association',
  roleEs: 'Presidente y CEO, Asociación Hondureño-Americana',
}
